import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ENDPOINTS,
  PROCESSING_STATUSES,
  REGIONS,
  VERIFY_BUCKETS,
  type AnalyticsByClientRow,
  type AnalyticsByReviewerRow,
  type AnalyticsDashboardResponse,
  type ProcessingStatus,
  type Region,
  type VerifyBucket,
} from '@clm/shared-types';

import { fetchAnalyticsDashboard } from '../api/analytics';
import { useCloudeqEntities } from '../hooks/useCloudeqEntities';
import { BarHistogram } from '../components/charts/BarHistogram';
import { Donut } from '../components/charts/Donut';
import { formatCount, formatHours, formatPercent } from '../components/charts/format';
import { StatTile } from '../components/charts/StatTile';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { requireSurface } from '../routes/surfaces';
import { ANALYTICS_FORBIDDEN_COPY, NO_MONEY_COPY, STATUS_LABELS, STATUS_TONES } from './analytics-shared';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';
import { AttentionStrip } from './analytics/AttentionStrip';
import { ContractsListPanel } from './analytics/ContractsListPanel';
import {
  DUE_PANEL_ID,
  KPI_EXPIRING_CAPTION,
  KPI_PANEL_ID,
  MIX_PANEL_ID,
  panelFromParams,
  panelToParam,
  panelTriggerId,
  samePanel,
  type DashboardPanel,
} from './analytics/labels';
import { RenewalsDueList } from './analytics/RenewalsDueList';

/**
 * Analytics **Dashboard** — CLM-81 (D-81-1…D-81-16; R-81-1).
 *
 * The KPI dashboard of the reference screenshot, re-mapped onto what CLM
 * records (D-81-11): Active, Expiring ≤ 30 / 60 / 90 d, Overdue obligations,
 * Alerts not acknowledged, Fields pending verification; a **time-to-verify**
 * histogram (intake → the 0046 `data_trust = 'Verified'` row, D-81-6) in place
 * of "time to sign"; a **status-mix** donut (D-81-7) in place of "sign rate";
 * **By client** and **By reviewer** tables (D-81-10) in place of "active users".
 * One read, `GET /v1/analytics/dashboard`, scoped on the API side. No period:
 * every KPI here is point-in-time (D-81-14). The Client options are the
 * response's own `by_client` rows (D-81-8).
 *
 * No money figures (D-81-2): the reference's totals are absent and the page
 * says so.
 *
 * CLM-90 (D-90-1…D-90-4): `/` lands here for every role. On top, the
 * **Needs attention** strip (`./analytics/AttentionStrip`) reads the response's
 * `attention` block — due < 30 / 60 / 90 d and past due on
 * `COALESCE(renewal_notice_date, expiry_date)`, per-tier sent / acknowledged /
 * not sent, fields pending, extraction failed — and the **Due for renewal**
 * list (`./analytics/RenewalsDueList`) reads `GET /v1/analytics/renewals-due`
 * with its own `?window=` state. Both take the Client and Region selects; the
 * CLM-81 sections below are unchanged.
 *
 * CLM-95 (D-95-3): this component is now the single owner of `?panel=`, the
 * one-at-a-time disclosure state spanning three otherwise-independent tile
 * groups — the strip's due tiles, the KPI row's Active / Expiring ≤ 30 d, and
 * the status-mix legend. Each group renders into a fixed-id wrapper
 * (`DUE_PANEL_ID` / `KPI_PANEL_ID` / `MIX_PANEL_ID`) so a tile's
 * `aria-controls` always resolves even while collapsed. Only the tiles R-95-1
 * names convert; every other KPI/region/entity tile stays exactly as before.
 */

const ENDPOINT_PATH = ENDPOINTS['analytics-dashboard'].path;
const PANEL = 'Analytics dashboard';

const BUCKET_LABELS: Readonly<Record<VerifyBucket, string>> = {
  lt_1d: '< 1 day',
  d1_3: '1–3 days',
  d3_7: '3–7 days',
  w1_3: '1–3 weeks',
  gt_3w: '> 3 weeks',
};

/**
 * CLM-108 (D-2.1-4) — `verified` counts `data_trust = 'Verified'` only: every
 * field settled by a person. A contract some of whose fields were settled by
 * policy is counted beside it, under "Auto-approved", never folded in — and
 * never dropped, which would make the row's figures stop adding up.
 */
const CLIENT_COLUMNS: readonly DataTableColumn<AnalyticsByClientRow>[] = [
  { id: 'client', header: 'Client', cell: (row) => row.display_name },
  { id: 'contracts', header: 'Contracts', cell: (row) => formatCount(row.contracts), cellClassName: 'num' },
  { id: 'pending', header: 'Pending', cell: (row) => formatCount(row.pending), cellClassName: 'num' },
  { id: 'verified', header: 'Verified', cell: (row) => formatCount(row.verified), cellClassName: 'num' },
  { id: 'auto_approved', header: 'Auto-approved', cell: (row) => formatCount(row.auto_approved), cellClassName: 'num' },
  { id: 'expiring_90', header: 'Expiring 90 d', cell: (row) => formatCount(row.expiring_90), cellClassName: 'num' },
];

/** A region or entity tile's caption: the person-verified count and, beside it, the policy-settled one. */
function trustCaption(row: { readonly verified: number; readonly auto_approved: number }): string {
  return `contracts · ${formatCount(row.verified)} verified by people · ${formatCount(row.auto_approved)} auto-approved`;
}

const REVIEWER_COLUMNS: readonly DataTableColumn<AnalyticsByReviewerRow>[] = [
  { id: 'reviewer', header: 'Reviewer', cell: (row) => row.actor_upn },
  { id: 'verifications', header: 'Verifications', cell: (row) => formatCount(row.verifications), cellClassName: 'num' },
  { id: 'corrections', header: 'Corrections', cell: (row) => formatCount(row.corrections), cellClassName: 'num' },
  { id: 'rejections', header: 'Rejections', cell: (row) => formatCount(row.rejections), cellClassName: 'num' },
  { id: 'avg', header: 'Avg time to verify', cell: (row) => formatHours(row.avg_hours_to_verify), cellClassName: 'num' },
];

export function AnalyticsDashboard() {
  const surface = requireSurface('analytics-dashboard');
  const [partyKey, setPartyKey] = useState<string>('all');
  /* CLM-87 — the PRIMARY party's region; AND-ed beside the client by the API (D-87-11). */
  const [region, setRegion] = useState<'all' | Region>('all');
  /* CLM-91 — the signing cloudEQ entity; the options are the registry rows (D-91-5), loaded once. */
  const [entity, setEntity] = useState<string>('all');
  const entities = useCloudeqEntities();

  /* CLM-95 (D-95-3) — the one shared disclosure state, `?panel=`. */
  const [params, setParams] = useSearchParams();
  const activePanel = panelFromParams(params.get('panel'));
  const togglePanel = (panel: DashboardPanel) => {
    setParams((previous) => {
      const merged = new URLSearchParams(previous);
      if (samePanel(activePanel, panel)) {
        merged.delete('panel');
        return merged;
      }
      merged.set('panel', panelToParam(panel));
      // Due tiles also drive `?window=`, so RenewalsDueList (which owns that
      // param itself) opens already filtered to the window that was clicked.
      if (panel.kind === 'due') {
        if (panel.window === '90') merged.delete('window');
        else merged.set('window', panel.window);
      }
      return merged;
    });
  };
  const collapsePanel = () => {
    // The section's own "Collapse" control unmounts itself along with the
    // section — return focus to the tile that opened it (its trigger button
    // never unmounts) rather than letting it fall to document.body.
    const returnFocusTo = activePanel === null ? null : panelTriggerId(activePanel);
    setParams((previous) => {
      const merged = new URLSearchParams(previous);
      merged.delete('panel');
      return merged;
    });
    if (returnFocusTo !== null) document.getElementById(returnFocusTo)?.focus();
  };

  const { state, reload } = usePanel<AnalyticsDashboardResponse>(
    (abort) =>
      fetchAnalyticsDashboard(
        {
          ...(partyKey === 'all' ? {} : { party_key: partyKey }),
          ...(region === 'all' ? {} : { region }),
          ...(entity === 'all' ? {} : { cloudeq_entity: entity }),
        },
        abort,
      ),
    [partyKey, region, entity],
  );

  const clients = state.kind === 'ready' ? state.response.by_client : [];

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="analytics-dashboard-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card analytics-section" aria-labelledby="analytics-dashboard-heading">
        <div className="analytics-head">
          <label>
            Client
            <select
              aria-label="Client"
              value={partyKey}
              onChange={(event) => {
                setPartyKey(event.target.value);
              }}
            >
              <option value="all">All clients</option>
              {clients.map((client) => (
                <option key={client.party_key} value={client.party_key}>
                  {client.display_name}
                </option>
              ))}
              {partyKey !== 'all' && !clients.some((client) => client.party_key === partyKey) ? (
                <option value={partyKey}>{partyKey}</option>
              ) : null}
            </select>
          </label>
          <label>
            Region
            <select
              aria-label="Region"
              value={region}
              onChange={(event) => {
                setRegion(event.target.value as 'all' | Region);
              }}
            >
              <option value="all">All regions</option>
              {REGIONS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            cloudEQ entity
            <select
              aria-label="cloudEQ entity"
              value={entity}
              onChange={(event) => {
                setEntity(event.target.value);
              }}
            >
              <option value="all">All cloudEQ entities</option>
              {entities.map((row) => (
                <option key={row.entity_key} value={row.entity_key}>
                  {row.display_name}
                </option>
              ))}
            </select>
          </label>
          <p className="mut">Counts are your scope only.</p>
        </div>
        <p className="mut">{NO_MONEY_COPY}</p>

        {state.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={ANALYTICS_FORBIDDEN_COPY}
          />
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <AttentionStrip
              attention={state.response.attention}
              kpis={state.response.kpis}
              statusMix={state.response.status_mix}
              activePanel={activePanel}
              onToggleDue={togglePanel}
            />
            <div id={DUE_PANEL_ID}>
              {activePanel?.kind === 'due' ? (
                <RenewalsDueList
                  partyKey={partyKey === 'all' ? null : partyKey}
                  region={region === 'all' ? null : region}
                  entityKey={entity === 'all' ? null : entity}
                  onCollapse={collapsePanel}
                />
              ) : null}
            </div>
            <div className="kpi-grid" role="group" aria-label="Key figures">
              <StatTile
                title="Active"
                n={state.response.kpis.active}
                tone="ok"
                id={panelTriggerId({ kind: 'kpi-active' })}
                expanded={samePanel(activePanel, { kind: 'kpi-active' })}
                panelId={KPI_PANEL_ID}
                onToggle={() => {
                  togglePanel({ kind: 'kpi-active' });
                }}
              />
              <StatTile
                title="Expiring ≤ 30 d"
                n={state.response.kpis.expiring_30}
                tone="acc"
                id={panelTriggerId({ kind: 'kpi-expiring-30' })}
                expanded={samePanel(activePanel, { kind: 'kpi-expiring-30' })}
                panelId={KPI_PANEL_ID}
                onToggle={() => {
                  togglePanel({ kind: 'kpi-expiring-30' });
                }}
              />
              <StatTile title="Expiring ≤ 60 d" n={state.response.kpis.expiring_60} tone="acc" />
              <StatTile title="Expiring ≤ 90 d" n={state.response.kpis.expiring_90} tone="acc" />
              <StatTile
                title="Overdue obligations"
                n={state.response.kpis.overdue_obligations}
                unit="obligations"
                tone={state.response.kpis.overdue_obligations > 0 ? 'bad' : undefined}
              />
              <StatTile
                title="Alerts not acknowledged"
                n={state.response.kpis.alerts_unacknowledged}
                unit="alerts"
                tone={state.response.kpis.alerts_unacknowledged > 0 ? 'bad' : undefined}
              />
              <StatTile title="Fields pending verification" n={state.response.kpis.fields_pending} unit="fields" tone="acc" />
              {/* CLM-85 (D-85-7) — one number: contracts converted with the tables_missing caveat. Attention, not failure. */}
              <StatTile
                title="Tables not detected"
                n={state.response.kpis.tables_missing}
                unit="contracts"
                tone={state.response.kpis.tables_missing > 0 ? 'acc' : undefined}
              />
            </div>
            <p className="mut">{KPI_EXPIRING_CAPTION}</p>
            <div id={KPI_PANEL_ID}>
              {activePanel?.kind === 'kpi-active' ? (
                <ContractsListPanel
                  title="Active"
                  emptyCopy="Nothing in your scope is Active."
                  partyKey={partyKey === 'all' ? null : partyKey}
                  region={region === 'all' ? null : region}
                  entityKey={entity === 'all' ? null : entity}
                  filters={{ lifecycleStatus: ['Active'] }}
                  onCollapse={collapsePanel}
                />
              ) : null}
              {activePanel?.kind === 'kpi-expiring-30' ? (
                <ContractsListPanel
                  title="Expiring ≤ 30 d"
                  emptyCopy="Nothing in your scope is expiring within 30 days."
                  partyKey={partyKey === 'all' ? null : partyKey}
                  region={region === 'all' ? null : region}
                  entityKey={entity === 'all' ? null : entity}
                  filters={{ expiringWithinDays: 30 }}
                  onCollapse={collapsePanel}
                />
              ) : null}
            </div>

            <div className="grid g2 analytics-section">
              <section className="card" aria-labelledby="analytics-ttv">
                <h3 id="analytics-ttv">Time to verify</h3>
                <BarHistogram
                  title="Time to verify"
                  buckets={VERIFY_BUCKETS.map((bucket) => ({
                    key: bucket,
                    label: BUCKET_LABELS[bucket],
                    n: state.response.time_to_verify.buckets[bucket],
                  }))}
                  emptyCopy="No contracts verified in scope yet"
                />
                {state.response.time_to_verify.unbucketed > 0 ? (
                  <p className="mut">
                    {formatCount(state.response.time_to_verify.unbucketed)} verified before the audit trigger — not bucketed
                  </p>
                ) : null}
              </section>
              <section className="card" aria-labelledby="analytics-mix">
                <h3 id="analytics-mix">Status mix</h3>
                <Donut
                  title="Status mix"
                  segments={PROCESSING_STATUSES.map((status) => ({
                    key: status,
                    label: STATUS_LABELS[status],
                    n: state.response.status_mix.counts[status],
                    tone: STATUS_TONES[status],
                  }))}
                  centre={state.response.status_mix.verified_share}
                  caption="verified by people"
                  onSegmentToggle={(status) => {
                    togglePanel({ kind: 'mix', status: status as ProcessingStatus });
                  }}
                  activeSegment={activePanel?.kind === 'mix' ? activePanel.status : undefined}
                  panelId={MIX_PANEL_ID}
                  segmentTriggerId={(status) => panelTriggerId({ kind: 'mix', status: status as ProcessingStatus })}
                />
                {/* CLM-108 (D-2.1-4) — the centre is human trust only (`data_trust = 'Verified'`); the share settled
                    partly by policy is named beside it, so the donut never counts it as verified nor hides it. */}
                <p className="mut" data-testid="status-mix-auto-approved">
                  {formatPercent(state.response.status_mix.auto_approved_share)} include auto-approved fields — settled
                  by policy, not by a person
                </p>
              </section>
            </div>
            <div id={MIX_PANEL_ID}>
              {activePanel?.kind === 'mix' ? (
                <ContractsListPanel
                  title={STATUS_LABELS[activePanel.status]}
                  emptyCopy={`Nothing in your scope is ${STATUS_LABELS[activePanel.status]}.`}
                  partyKey={partyKey === 'all' ? null : partyKey}
                  region={region === 'all' ? null : region}
                  entityKey={entity === 'all' ? null : entity}
                  filters={{ processingStatus: [activePanel.status] }}
                  onCollapse={collapsePanel}
                />
              ) : null}
            </div>

            <div className="grid g2 analytics-section">
              <section className="card" aria-labelledby="analytics-by-client">
                <h3 id="analytics-by-client">By client</h3>
                <DataTable
                  caption="Contracts by client"
                  columns={CLIENT_COLUMNS}
                  rows={state.response.by_client}
                  rowKey={(row) => row.party_key}
                  empty="No contracts in scope"
                />
              </section>
              <section className="card" aria-labelledby="analytics-by-reviewer">
                <h3 id="analytics-by-reviewer">By reviewer</h3>
                <DataTable
                  caption="Field verifications by reviewer"
                  columns={REVIEWER_COLUMNS}
                  rows={state.response.by_reviewer}
                  rowKey={(row) => row.actor_upn}
                  empty="No fields verified in scope yet"
                />
              </section>
            </div>

            {/* CLM-87 (D-87-11) — five fixed tiles from the API's VALUES spine: the four regions, then
                Unassigned (a client nobody has placed — attention when it counts anything). */}
            <section className="card analytics-section" aria-labelledby="analytics-by-region">
              <h3 id="analytics-by-region">By region</h3>
              <div className="kpi-grid" role="group" aria-label="Contracts by region">
                {state.response.by_region.map((row) => (
                  <StatTile
                    key={row.region ?? 'unassigned'}
                    title={row.region ?? 'Unassigned'}
                    n={row.contracts}
                    unit={trustCaption(row)}
                    tone={row.region === null && row.contracts > 0 ? 'acc' : undefined}
                  />
                ))}
              </div>
            </section>

            {/* CLM-91 (D-91-5) — the registry spine from the API: every active cloudEQ entity in sort_order
                with its display_name (the fifth is TBC, so nothing here is a constant), then Unassigned —
                a contract the in-tenant scan could not place (attention when it counts anything). */}
            <section className="card analytics-section" aria-labelledby="analytics-by-cloudeq-entity">
              <h3 id="analytics-by-cloudeq-entity">By cloudEQ entity</h3>
              <div className="kpi-grid" role="group" aria-label="Contracts by cloudEQ entity">
                {state.response.by_cloudeq_entity.map((row) => (
                  <StatTile
                    key={row.entity_key ?? 'unassigned'}
                    title={row.display_name ?? 'Unassigned'}
                    n={row.contracts}
                    unit={trustCaption(row)}
                    tone={row.entity_key === null && row.contracts > 0 ? 'acc' : undefined}
                  />
                ))}
              </div>
            </section>
          </>
        ) : null}
      </section>
    </>
  );
}
